import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { Locale } from "../../shared/dto";
import { staffMutation, isOfflineMutation } from "./model";
import "./admin-unlock.css";
import { clockFields, intlLanguage } from "../date-format";

export const GLOBAL_UNLOCK_KINDS = ["onboarding", "marketing"] as const;
export type GlobalUnlockKind = (typeof GLOBAL_UNLOCK_KINDS)[number];

export interface GlobalUnlockLink {
  kind: GlobalUnlockKind;
  slug: string;
  path: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface GlobalUnlockHistoryEntry {
  kind: GlobalUnlockKind;
  slug: string;
  previousSlug: string | null;
  changedAt: string;
  changedBy: string | null;
}

type RowState =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved" }
  | { status: "copied" }
  // S2-01: "the save never left this machine" is a different sentence from
  // "the server refused it", because only one of them is the reader's to fix.
  | { status: "error"; key: "errorInvalid" | "errorTaken" | "errorSave" | "errorCopy" | "errorOffline" };

/**
 * F38: the unlock link is the same for every client at once, because calls are
 * group calls. This panel is the only place the slug changes, so it shows the
 * live value, copies the whole URL in one click, and keeps the change history
 * visible: an admin who rotates the slug mid day must be able to see which
 * value was live when a client redeemed.
 */
export function AdminGlobalUnlock(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
}) {
  const [links, setLinks] = useState<GlobalUnlockLink[]>([]);
  const [history, setHistory] = useState<GlobalUnlockHistoryEntry[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const response = await fetch("/api/admin/unlock-links", { credentials: "include" });
      const body = await response.json() as {
        ok?: boolean;
        links?: GlobalUnlockLink[];
        history?: GlobalUnlockHistoryEntry[];
      };
      if (!response.ok || !body.ok || !Array.isArray(body.links)) throw new Error("unlock_links_load_failed");
      setLinks(body.links);
      setHistory(body.history ?? []);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="admin-global-unlock" aria-labelledby="admin-global-unlock-title">
      <div className="admin-call-overview-head">
        <div>
          <p className="panel-label">{props.t("staffConsole.adminCalls.globalUnlock.kicker")}</p>
          <h4 id="admin-global-unlock-title">{props.t("staffConsole.adminCalls.globalUnlock.title")}</h4>
          <p>{props.t("staffConsole.adminCalls.globalUnlock.hint")}</p>
        </div>
      </div>
      {loadState === "loading" && <p>{props.t("staffConsole.adminCalls.globalUnlock.loading")}</p>}
      {loadState === "error" && (
        <div className="admin-call-load-error" role="alert">
          <p className="error-message">{props.t("staffConsole.adminCalls.globalUnlock.errorLoad")}</p>
          <button className="secondary-button" type="button" onClick={() => void load()}>{props.t("common.retry")}</button>
        </div>
      )}
      {loadState === "ready" && links.map((link) => (
        <GlobalUnlockRow key={link.kind} t={props.t} locale={props.locale} csrfToken={props.csrfToken}
          link={link} lastChange={lastGlobalUnlockChange(history, link.kind)}
          onSaved={(saved) => {
            setLinks((current) => current.map((entry) => entry.kind === saved.kind ? saved : entry));
            void load();
          }} />
      ))}
      {loadState === "ready" && (
        <GlobalUnlockHistory t={props.t} locale={props.locale} entries={history} />
      )}
    </section>
  );
}

/**
 * The newest recorded rename of one link kind, or null when the slug has never
 * been changed (audit S2-10).
 *
 * The row used to print "Poslední změna: 5. 8. 2026 13:47 · Alex Automation"
 * sixty pixels above "HISTORIE ZMĚN · Zatím se nic neměnilo", because the two
 * lines read two different tables: `updatedAt` is the stamp of the row that
 * CREATED the link, the history is the rename log. In a panel whose whole
 * point is that the old link stops working the instant somebody edits it, an
 * admin cannot be left guessing which of the two sentences is true. Both come
 * from the history now, and a link nobody has touched says so.
 */
export function lastGlobalUnlockChange(
  history: GlobalUnlockHistoryEntry[],
  kind: GlobalUnlockKind
): GlobalUnlockHistoryEntry | null {
  return history
    .filter((entry) => entry.kind === kind)
    .reduce<GlobalUnlockHistoryEntry | null>(
      (newest, entry) =>
        !newest || Date.parse(entry.changedAt) > Date.parse(newest.changedAt) ? entry : newest,
      null
    );
}

export function GlobalUnlockRow(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  link: GlobalUnlockLink;
  /** Null when the history holds no rename of this kind. */
  lastChange?: GlobalUnlockHistoryEntry | null;
  onSaved: (link: GlobalUnlockLink) => void;
}) {
  const [slug, setSlug] = useState(props.link.slug);
  const [state, setState] = useState<RowState>({ status: "idle" });
  useEffect(() => { setSlug(props.link.slug); }, [props.link.slug]);

  const dirty = slug.trim() !== props.link.slug;
  const inputId = `global-unlock-${props.link.kind}`;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!dirty) return;
    setState({ status: "saving" });
    const response = await staffMutation(`/api/admin/unlock-links/${props.link.kind}`, {
      method: "PUT", csrfToken: props.csrfToken, body: { slug }
    });
    if (response.ok) {
      const body = await response.json() as { link: GlobalUnlockLink };
      setState({ status: "saved" });
      props.onSaved(body.link);
      return;
    }
    setState({
      status: "error",
      key: isOfflineMutation(response)
        ? "errorOffline"
        : response.status === 409 ? "errorTaken" : response.status === 400 ? "errorInvalid" : "errorSave"
    });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(new URL(props.link.path, window.location.origin).toString());
      setState({ status: "copied" });
    } catch {
      setState({ status: "error", key: "errorCopy" });
    }
  }

  return (
    <article className="admin-global-unlock-row">
      <form onSubmit={(event) => void save(event)}>
        <p className="panel-label">{props.t(`staffConsole.adminCalls.globalUnlock.${props.link.kind}`)}</p>
        <label htmlFor={inputId}>
          <span>{props.t("staffConsole.adminCalls.globalUnlock.slugLabel")}</span>
          <input id={inputId} type="text" value={slug} inputMode="text" autoComplete="off"
            disabled={state.status === "saving"} onChange={(event) => {
              setSlug(event.target.value);
              setState({ status: "idle" });
            }} />
        </label>
        <p className="admin-global-unlock-hint">{props.t("staffConsole.adminCalls.globalUnlock.slugHint")}</p>
        <div className="admin-global-unlock-live">
          <a href={props.link.path} target="_blank" rel="noreferrer">{props.link.path}</a>
          <button className="secondary-button" type="button" disabled={state.status === "saving"}
            onClick={() => void copy()}>
            {props.t("staffConsole.adminCalls.globalUnlock.copy")}
          </button>
        </div>
        <div className="admin-global-unlock-actions">
          <button className="primary-button" type="submit" disabled={!dirty || state.status === "saving"}>
            {props.t(`staffConsole.adminCalls.globalUnlock.${state.status === "saving" ? "saving" : "save"}`)}
          </button>
          {/* S2-19: a control the reader cannot press says why, next to
              itself. A `title` never reaches them: a disabled element takes
              no pointer. */}
          {!dirty && state.status !== "saving" && (
            <span className="admin-global-unlock-meta">{props.t("staffConsole.adminCalls.globalUnlock.saveIdle")}</span>
          )}
          <span className="admin-global-unlock-meta">
            {props.lastChange
              ? `${props.t("staffConsole.adminCalls.globalUnlock.lastChange")}: `
                + formatChangedAt(props.lastChange.changedAt, props.locale)
                + (props.lastChange.changedBy ? ` · ${props.lastChange.changedBy}` : "")
              : props.t("staffConsole.adminCalls.globalUnlock.neverChanged")}
          </span>
        </div>
        <div className="admin-call-action-feedback" aria-live="polite">
          {state.status === "saved" && <p className="success-message">{props.t("staffConsole.adminCalls.globalUnlock.saved")}</p>}
          {state.status === "copied" && <p className="success-message">{props.t("staffConsole.adminCalls.globalUnlock.copied")}</p>}
          {state.status === "error" && (
            <p className="error-message" role="alert">{props.t(`staffConsole.adminCalls.globalUnlock.${state.key}`)}</p>
          )}
        </div>
      </form>
    </article>
  );
}

export function GlobalUnlockHistory(props: {
  t: (key: string) => string;
  locale: Locale;
  entries: GlobalUnlockHistoryEntry[];
}) {
  return (
    <div className="admin-global-unlock-history">
      <p className="panel-label">{props.t("staffConsole.adminCalls.globalUnlock.history")}</p>
      {!props.entries.length && <p className="staff-empty">{props.t("staffConsole.adminCalls.globalUnlock.historyEmpty")}</p>}
      {props.entries.length > 0 && (
        <ul>
          {props.entries.map((entry) => (
            <li key={`${entry.kind}-${entry.changedAt}-${entry.slug}`}>
              <strong>{props.t(`staffConsole.adminCalls.globalUnlock.${entry.kind}`)}</strong>
              <code>{entry.slug}</code>
              {entry.previousSlug && (
                <span>
                  {props.t("staffConsole.adminCalls.globalUnlock.historyReplaced")}
                  {" "}
                  <code>{entry.previousSlug}</code>
                </span>
              )}
              <small>
                {formatChangedAt(entry.changedAt, props.locale)}
                {entry.changedBy ? ` · ${entry.changedBy}` : ""}
              </small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function formatChangedAt(value: string, locale: Locale): string {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    day: "numeric", month: "numeric", year: "numeric", ...clockFields(locale)
  }).format(new Date(parsed));
}
